import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, ChevronRight, FileText, CheckCircle2, CalendarDays } from 'lucide-react';
import api from '@/lib/api';
import CalendarView from '@/components/CalendarView';
import StatusDot from '@/components/StatusDot';

const fmtDate = (iso) => {
  if (!iso) return '\u00A0';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '\u00A0' : d.toLocaleDateString();
};

function ProjectTable({ projects, onOpen, emptyMsg }) {
  return (
    <div className="bg-transparent">
      <Table>
        <TableHeader>
          <TableRow className="border-border hover:bg-transparent">
            <TableHead className="text-xs text-muted-foreground">Number</TableHead>
            <TableHead className="text-xs text-muted-foreground">Name</TableHead>
            <TableHead className="text-xs text-muted-foreground">Client</TableHead>
            <TableHead className="text-xs text-muted-foreground">Status</TableHead>
            <TableHead className="text-xs text-muted-foreground">Last Modified</TableHead>
            <TableHead className="text-xs text-muted-foreground text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {projects.map(p => (
            <TableRow key={p.id} className="group border-border hover:bg-secondary/30 cursor-pointer" onClick={() => onOpen(p)} data-testid={`project-row-${p.id}`}>
              <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{p.project_number}</TableCell>
              <TableCell className="text-sm" style={{ color: '#010A21' }}>{p.name}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{p.client_name}</TableCell>
              <TableCell><StatusDot status={p.status} /></TableCell>
              <TableCell className="text-sm text-muted-foreground" style={{ fontFamily: "'DM Mono', monospace" }}>{fmtDate(p.updated_at || p.created_at)}</TableCell>
              <TableCell className="text-right"><ChevronRight className="h-4 w-4 text-muted-foreground inline opacity-0 group-hover:opacity-100 transition-opacity" /></TableCell>
            </TableRow>
          ))}
          {projects.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">{emptyMsg}</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [pageTab, setPageTab] = useState('active');

  useEffect(() => {
    api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => setProjects([]));
  }, []);

  // Backward-compatible deep link: /projects?open=<id> now redirects to the full-page record view
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      setSearchParams({}, { replace: true });
      navigate(`/projects/${openId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = (p) => navigate(`/projects/${p.id}`);

  const searchFilter = (p) =>
    (p.project_number || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.client_name || '').toLowerCase().includes(search.toLowerCase());

  const activeProjects = projects.filter(p => p.status !== 'Completed' && p.status !== 'Archived').filter(searchFilter);
  const completedProjects = projects.filter(p => p.status === 'Completed').filter(searchFilter);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-semibold text-lg tracking-tight">Projects</h1>
          <p className="text-sm text-muted-foreground">{projects.length} total projects</p>
        </div>
      </div>

      <Tabs value={pageTab} onValueChange={setPageTab}>
        <TabsList className="bg-secondary border border-border" data-testid="projects-page-tabs">
          <TabsTrigger value="active" className="text-xs data-[state=active]:bg-background gap-1" data-testid="tab-active">
            <FileText className="h-3 w-3" /> Active ({projects.filter(p => p.status !== 'Completed' && p.status !== 'Archived').length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="text-xs data-[state=active]:bg-background gap-1" data-testid="tab-completed">
            <CheckCircle2 className="h-3 w-3" /> Completed ({projects.filter(p => p.status === 'Completed').length})
          </TabsTrigger>
          <TabsTrigger value="calendar" className="text-xs data-[state=active]:bg-background gap-1" data-testid="tab-calendar">
            <CalendarDays className="h-3 w-3" /> Calendar
          </TabsTrigger>
        </TabsList>

        {(pageTab === 'active' || pageTab === 'completed') && (
          <div className="relative max-w-sm mt-3">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="projects-search" />
          </div>
        )}

        <TabsContent value="active" className="mt-3">
          <ProjectTable projects={activeProjects} onOpen={openDetail} emptyMsg="No active projects. Convert a contract to create one." />
        </TabsContent>

        <TabsContent value="completed" className="mt-3">
          <ProjectTable projects={completedProjects} onOpen={openDetail} emptyMsg="No completed projects yet." />
        </TabsContent>

        <TabsContent value="calendar" className="mt-3">
          <CalendarView />
        </TabsContent>
      </Tabs>
    </div>
  );
}
